import { onlineManager } from '@tanstack/react-query';
import type { Middleware } from 'openapi-fetch';
import { useSyncExternalStore } from 'react';

import { SW_CACHE_HEADER } from '../pwa/swRules';

/**
 * Conectividad real del cliente (S01 "Sin conexión"). Junta tres señales:
 * - el navegador (`navigator.onLine`): un `false` es confiable;
 * - el API sin respuesta (error de red): el celular dice tener señal pero nada llega, lo normal en
 *   la calle con mala cobertura. Cuenta como "sin conexión" hasta que el API vuelve a responder;
 * - el service worker respondió desde su caché (header `X-Parche-Cache`): lo que se ve es de antes.
 *
 * Mientras no hay conexión se sondea `/healthz` cada cierto tiempo; "Reintentar" sondea al instante.
 */
export interface ConnectivityState {
  /** `navigator.onLine`. */
  browserOnline: boolean;
  /** El API no respondió (error de red) y todavía no vuelve a responder. */
  unreachable: boolean;
  /** Epoch en ms de lo más viejo que el service worker sirvió desde su caché, o `null`. */
  cachedSince: number | null;
}

/** Header que agrega el service worker (`src/pwa/swCore.ts`) a una respuesta servida desde su caché. */
export { SW_CACHE_HEADER };

/** Sonda liviana del API (sin caché del service worker ni del navegador). */
export const PROBE_URL = '/healthz';
const PROBE_TIMEOUT_MS = 5000;
/** Cada cuánto se vuelve a sondear mientras no hay conexión. */
export const PROBE_INTERVAL_MS = 20_000;

type Listener = () => void;

const listeners = new Set<Listener>();
let unreachable = false;
let cachedSince: number | null = null;
let snapshot: ConnectivityState = { browserOnline: true, unreachable: false, cachedSince: null };
let windowBound = false;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probing: Promise<boolean> | null = null;

function browserOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Estado actual (el mismo objeto mientras nada cambie, como pide `useSyncExternalStore`). */
export function getConnectivity(): ConnectivityState {
  const online = browserOnline();
  if (snapshot.browserOnline !== online || snapshot.unreachable !== unreachable || snapshot.cachedSince !== cachedSince) {
    snapshot = { browserOnline: online, unreachable, cachedSince };
  }
  return snapshot;
}

export function isOnline(state: ConnectivityState): boolean {
  return state.browserOnline && !state.unreachable && state.cachedSince === null;
}

function emit(): void {
  for (const listener of [...listeners]) listener();
}

function bindWindow(): void {
  if (windowBound || typeof window === 'undefined') return;
  windowBound = true;
  window.addEventListener('online', () => {
    emit();
    // El sistema recuperó la red; el API se confirma con la sonda.
    if (unreachable || cachedSince !== null) void probeConnection();
  });
  window.addEventListener('offline', emit);
}

export function subscribeConnectivity(listener: Listener): () => void {
  bindWindow();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function scheduleProbe(): void {
  if (probeTimer !== null || typeof window === 'undefined') return;
  probeTimer = setTimeout(() => {
    probeTimer = null;
    if (unreachable || cachedSince !== null) void probeConnection();
  }, PROBE_INTERVAL_MS);
}

function stopProbing(): void {
  if (probeTimer !== null) clearTimeout(probeTimer);
  probeTimer = null;
}

/** El API respondió (cualquier estado HTTP): hay conexión. */
export function reportReachable(): void {
  stopProbing();
  if (!unreachable && cachedSince === null) return;
  unreachable = false;
  cachedSince = null;
  emit();
}

/** Una petición al API no tuvo respuesta (error de red, no una cancelación). */
export function reportUnreachable(): void {
  scheduleProbe();
  if (unreachable) return;
  unreachable = true;
  emit();
}

/** El service worker sirvió una respuesta guardada el `savedAt` (epoch en ms). */
export function reportCachedResponse(savedAt: number): void {
  scheduleProbe();
  const at = Number.isFinite(savedAt) ? savedAt : Date.now();
  const next = cachedSince === null ? at : Math.min(cachedSince, at);
  if (next === cachedSince) return;
  cachedSince = next;
  emit();
}

/** Lee una respuesta del API: si vino de la caché del service worker o de la red. */
export function reportResponse(response: Response): void {
  const cached = response.headers.get(SW_CACHE_HEADER);
  if (cached) reportCachedResponse(Number(cached));
  else reportReachable();
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

/**
 * Sondea el API ahora (una sola sonda a la vez). Resuelve `true` si respondió. Es lo que hace
 * "Reintentar conexión" antes de volver a pedir los datos.
 */
export function probeConnection(): Promise<boolean> {
  probing ??= (async () => {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();
    const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
    try {
      await fetch(PROBE_URL, { cache: 'no-store', signal: controller?.signal, headers: { Accept: 'application/json' } });
      reportReachable();
      return true;
    } catch {
      reportUnreachable();
      return false;
    } finally {
      if (timer !== null) clearTimeout(timer);
      probing = null;
    }
  })();
  return probing;
}

/** Middleware de openapi-fetch: cada respuesta o error de red del API actualiza la conectividad. */
export const connectivityMiddleware: Middleware = {
  onResponse({ response }) {
    reportResponse(response);
    return undefined;
  },
  onError({ error }) {
    if (!isAbortError(error)) reportUnreachable();
    return undefined;
  },
};

/**
 * TanStack Query sigue esta conectividad (y no solo los eventos del navegador): sin conexión las
 * consultas y mutaciones esperan en pausa en vez de fallar una y otra vez. `alsoOffline` suma otra
 * condición (la sesión sin conexión, que espera a renovarse antes de volver a pedir datos).
 */
export function bindQueryOnlineManager(alsoOffline: () => boolean = () => false, subscribeExtra?: (listener: Listener) => () => void): void {
  onlineManager.setEventListener((setOnline) => {
    const update = () => setOnline(isOnline(getConnectivity()) && !alsoOffline());
    update();
    const unsubscribe = subscribeConnectivity(update);
    const unsubscribeExtra = subscribeExtra?.(update);
    return () => {
      unsubscribe();
      unsubscribeExtra?.();
    };
  });
}

const SERVER_SNAPSHOT: ConnectivityState = { browserOnline: true, unreachable: false, cachedSince: null };

export function useConnectivity(): ConnectivityState {
  return useSyncExternalStore(subscribeConnectivity, getConnectivity, () => SERVER_SNAPSHOT);
}

/** Hay conexión (navegador y API). */
export function useOnline(): boolean {
  return isOnline(useConnectivity());
}

/** Solo para pruebas: vuelve al estado inicial. */
export function resetConnectivity(): void {
  stopProbing();
  unreachable = false;
  cachedSince = null;
  probing = null;
  emit();
}
